import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { IconButton, SelectChangeEvent } from "@mui/material";

import TextField from "@app/analytics/components/TextField";
import Dropdown from "@app/common/components/Dropdown";
import DropdownMenuItem from "@app/common/components/Dropdown/DropdownMenuItem";
import { getSelectedFromOptions } from "@app/common/components/Dropdown/utils";

import { Row } from "@rui/Stack";

const DEFAULT_ROLE = {
  label: "User",
  value: "member",
};

const ROLE_MENU_ITEMS = [
  {
    label: "Team Manager",
    value: "manager",
  },
  DEFAULT_ROLE,
];

interface InputProps {
  name: string;
  error?: string;
  selected?: string;
  disabled?: boolean;
  onChange:
    | ((event: React.ChangeEvent<HTMLInputElement>) => void)
    | ((value: string) => void);
}
const DESELECT_VALUE = { value: "", label: "None" };

interface InviteWorkspaceMemberInputsProps {
  allowRemove: boolean;
  email: InputProps;
  onRemove?: () => void;
  role: InputProps;
  team: InputProps;
  teams: { label: string; value: string }[];
}

const InviteWorkspaceMemberInputs = ({
  allowRemove,
  email,
  onRemove,
  role,
  team,
  teams,
}: InviteWorkspaceMemberInputsProps) => {
  const handleTeamChange = (event: SelectChangeEvent<any>) => {
    team.onChange(event.target.value);
  };

  const handleRoleChange = (event: SelectChangeEvent<any>) => {
    role.onChange(event.target.value);
  };

  const handleEmailChange = (event: any) => {
    email.onChange(event.target.value);
  };

  const { error: emailError, ...emailInputProps } = email;

  return (
    <Row sx={{ width: "100%" }} alignItems="start">
      <Row sx={{ flexGrow: 1 }} spacing={1}>
        <TextField
          error={Boolean(emailError)}
          helperText={emailError}
          label={allowRemove ? undefined : "EMAIL"}
          InputProps={{
            ...emailInputProps,
            onChange: handleEmailChange,
            placeholder: "example@email.com",
          }}
          sx={{
            maxWidth: "246px",
            "& .MuiOutlinedInput-root input": {
              height: 28,
              fontSize: "1rem",
            },
            "& input.Mui-disabled": {
              "::placeholder": {
                opacity: 1,
              },
              backgroundColor: "#F0F0F0",
            },
          }}
        />
        <Dropdown
          error={Boolean(team.error)}
          inputProps={{ error: Boolean(team.error), helperText: team.error }}
          label={allowRemove ? undefined : "Add to Team"}
          selectedItem={getSelectedFromOptions(teams, `${team.selected}`)}
          name="team-select"
          placeholder="Select team..."
          wrapperSx={{
            ".MuiFormHelperText-root": {
              maxWidth: "170px",
            },
          }}
          sx={{
            width: "185px",
            ".MuiSelect-select": {
              color: "shades.neutral.600",
              fontSize: "1rem",
            },
          }}
          onChange={handleTeamChange}
        >
          {(team.selected ? [DESELECT_VALUE, ...teams] : teams).map((item) => {
            const isSelectedItem = item.value === team.selected;
            return (
              <DropdownMenuItem
                key={item.value}
                {...item}
                selected={isSelectedItem}
              />
            );
          })}
        </Dropdown>
        <Dropdown
          error={Boolean(role.error)}
          inputProps={{ error: Boolean(role.error), helperText: role.error }}
          label={allowRemove ? undefined : "Team Role"}
          disabled={role.disabled}
          selectedItem={getSelectedFromOptions(
            ROLE_MENU_ITEMS,
            role.selected ?? DEFAULT_ROLE.value
          )}
          name="team-role-select"
          sx={{
            width: "180px",
            ".MuiSelect-select": {
              color: "shades.neutral.600",
              fontSize: "1rem",
            },
          }}
          onChange={handleRoleChange}
        >
          {ROLE_MENU_ITEMS.map((item) => {
            const isSelectedItem =
              item.value === role.selected || item.value === DEFAULT_ROLE.value;
            return (
              <DropdownMenuItem
                key={item.value}
                {...item}
                selected={isSelectedItem}
              />
            );
          })}
        </Dropdown>
      </Row>
      <IconButton
        onClick={onRemove}
        sx={{
          visibility: allowRemove && onRemove ? "visible" : "hidden",
          width: "40px",
          height: "40px",
          mt: "2px",
          ml: 1,
          color: "shades.primary.500",
        }}
      >
        <CloseIcon />
      </IconButton>
    </Row>
  );
};

export default InviteWorkspaceMemberInputs;
